import type { Position } from 'geojson';
import { formatToUSDateTimeWith12Hour } from 'utils/dateTimeUtils/dateFormatUtil';
import escapeXml from 'utils/escapeXml';
import {
    COORDINATES_CLOSE,
    COORDINATES_OPEN,
    LINE_STRING_CLOSE,
    LINE_STRING_OPEN,
    NAME_CLOSE,
    NAME_OPEN,
    PLACEMARK_CLOSE,
    PLACEMARK_OPEN
} from 'utils/generateKml/constants';

import type { FlightResponse, GenerateKmlPlacemark } from './types';

const LONGITUDE_INDEX = 0;
const LATITUDE_INDEX = 1;
const MIN_COORDINATES = 2;
const MIN_PATH_DISTANCE_DEGREES = 0.00002;

const getPositions = (flight: FlightResponse): Position[] => {
    const positions = flight.flightPath?.coordinates as unknown as Position[] | undefined;

    if (!positions || positions.length < MIN_COORDINATES) {
        return [];
    }

    return positions;
};

const hasMinimumSpan = (positions: Position[]): boolean => {
    const first = positions[0];

    return positions.some(position => {
        const lonDelta = (position[LONGITUDE_INDEX] ?? 0) - (first[LONGITUDE_INDEX] ?? 0);
        const latDelta = (position[LATITUDE_INDEX] ?? 0) - (first[LATITUDE_INDEX] ?? 0);

        return Math.sqrt(lonDelta * lonDelta + latDelta * latDelta) >= MIN_PATH_DISTANCE_DEGREES;
    });
};

const formatCoordinates = (positions: Position[]): string =>
    positions
        .map(position => `${position[LONGITUDE_INDEX]},${position[LATITUDE_INDEX]},0`)
        .join(' ');

const generateKmlPlacemark: GenerateKmlPlacemark = flight => {
    const positions = getPositions(flight);

    if (positions.length < MIN_COORDINATES || !hasMinimumSpan(positions)) {
        return null;
    }

    const dateTime = formatToUSDateTimeWith12Hour(flight.startTime);

    return [
        PLACEMARK_OPEN,
        `  ${NAME_OPEN}${escapeXml(dateTime)} Flight${NAME_CLOSE}`,
        `  ${LINE_STRING_OPEN}`,
        `    ${COORDINATES_OPEN}${formatCoordinates(positions)}${COORDINATES_CLOSE}`,
        `  ${LINE_STRING_CLOSE}`,
        PLACEMARK_CLOSE
    ].join('\n');
};

export default generateKmlPlacemark;
